import React from 'react';
import { ROUTE_VIEW_MODE, type RouteViewMode } from '../../../../constants/booking';

type DayRoutePlaybackOptions = {
  routeViewMode: RouteViewMode;
  locationHistoryLength: number;
};

type DayRoutePlaybackHook = {
  index: number;
  progress: number;
  isPlaying: boolean;
  speed: number;
  setSpeed: (speed: number) => void;
  indexRef: React.MutableRefObject<number>;
  progressRef: React.MutableRefObject<number>;
  isPlayingRef: React.MutableRefObject<boolean>;
  play: () => void;
  pause: () => void;
  stepBack: () => void;
  stepForward: () => void;
  reset: () => void;
};

const DAY_ROUTE_BASE_MS = 800; // 1x = 800ms per segment

export function useDayRoutePlayback(options: DayRoutePlaybackOptions): DayRoutePlaybackHook {
  const { routeViewMode, locationHistoryLength } = options;

  const [index, setIndex] = React.useState<number>(-1);
  const [progress, setProgress] = React.useState<number>(0);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [speed, setSpeed] = React.useState<number>(1);

  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const rafRef = React.useRef<number | null>(null);
  const segmentStartRef = React.useRef<number>(0);
  const isPlayingRef = React.useRef(false);
  const speedRef = React.useRef(1);
  speedRef.current = speed;
  const indexRef = React.useRef(-1);
  indexRef.current = index;
  const progressRef = React.useRef(0);
  progressRef.current = progress;
  const lengthRef = React.useRef(0);
  lengthRef.current = locationHistoryLength;

  const clearTimers = React.useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const reset = React.useCallback(() => {
    isPlayingRef.current = false;
    setIsPlaying(false);
    setIndex(-1);
    setProgress(0);
    progressRef.current = 0;
    clearTimers();
  }, [clearTimers]);

  // Clear day route playback when leaving Day Route or when route data changes
  React.useEffect(() => {
    if (routeViewMode !== ROUTE_VIEW_MODE.DAY || !locationHistoryLength) {
      reset();
    }
    return () => {
      clearTimers();
    };
  }, [routeViewMode, locationHistoryLength, reset, clearTimers]);

  const stepBack = React.useCallback(() => {
    setProgress(0);
    setIndex((i) => {
      const len = lengthRef.current;
      if (len === 0) return -1;
      const next = i <= 0 ? 0 : i - 1;
      return next;
    });
  }, []);

  const stepForward = React.useCallback(() => {
    setProgress(0);
    setIndex((i) => {
      const len = lengthRef.current;
      if (len === 0) return -1;
      const next = i < 0 ? 0 : Math.min(len - 1, i + 1);
      return next;
    });
  }, []);

  const play = React.useCallback(() => {
    const len = lengthRef.current;
    if (len === 0) return;

    // If playback has not started or already finished, restart from the beginning
    setIndex((i) => {
      if (i < 0 || i >= len - 1) {
        return 0;
      }
      return i;
    });
    setProgress(0);
    setIsPlaying(true);
    isPlayingRef.current = true;
    segmentStartRef.current = performance.now();
    clearTimers();

    const tick = () => {
      if (!isPlayingRef.current) return;
      const length = lengthRef.current;
      const currentSpeed = speedRef.current;
      const segmentDuration = DAY_ROUTE_BASE_MS / Math.max(0.25, currentSpeed);
      const elapsed = performance.now() - segmentStartRef.current;
      const nextProgress = Math.min(1, elapsed / segmentDuration);
      progressRef.current = nextProgress;
      setProgress(nextProgress);

      if (nextProgress >= 1) {
        setIndex((prev) => {
          if (length === 0) return prev;
          if (prev >= length - 1) {
            isPlayingRef.current = false;
            setIsPlaying(false);
            progressRef.current = 1;
            setProgress(1);
            return prev;
          }
          segmentStartRef.current = performance.now();
          const next = prev + 1;
          indexRef.current = next;
          progressRef.current = 0;
          return next;
        });
        setProgress(0);
      }

      if (isPlayingRef.current) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
  }, [clearTimers]);

  const pause = React.useCallback(() => {
    isPlayingRef.current = false;
    setIsPlaying(false);
    clearTimers();
  }, [clearTimers]);

  return {
    index,
    progress,
    isPlaying,
    speed,
    setSpeed,
    indexRef,
    progressRef,
    isPlayingRef,
    play,
    pause,
    stepBack,
    stepForward,
    reset,
  };
}

